/** @format */

import { useDispatch } from 'react-redux';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import Filter from '../../../../../interfaces/forms/Filter';
import Order from '../../../../../interfaces/forms/Order';
import { ItemLoaders } from '../../../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../../../helpers/FormGeneral';
import { DISCOUNT_CONSTANTS } from '../../../../../modules/discounts/reducers';
import { configDefault } from '../../../../../core/config';
import { platformGroupsConstants } from '../../../../../modules/platformGroups/reducers';
import GridContainer from '../../../../../components/Grid/GridContainer';
import GridItem from '../../../../../components/Grid/GridItem';
import Card from '../../../../../components/Card/Card';
import CardBody from '../../../../../components/Card/CardBody';
import { makeStyles, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import FilterList from '../../../../../components/Filter/FilterList';
import TableHeaderContentCell from '../../../../../components/Table/TableHeaderContentCell';
import RemoveButton from '../../../../../components/Buttons/RemoveButton';
import CustomTextButtom from '../../../../../components/Buttons/CustomTextButton';
import AddButton from '../../../../../components/Buttons/AddButton';
import Pager from '../../../../../components/Adescom/PagerRange';
import adescomStyle from '../../../../../assets/jss/adescom/adescomStyle';
import { FilterListReducerFieldName } from '../../../../../modules/filter/FilterListReducers';
import { FilterReducerFieldName } from '../../../../../modules/filter/FilterReducers';

interface CheckZoneCodeProps {
    simCardServiceTypeId: number;
    simCardServiceType: string;
}

export const CheckZoneCode: FunctionComponent<CheckZoneCodeProps> = ({ simCardServiceTypeId, simCardServiceType }): React.ReactElement => {
    const useStyles = makeStyles(adescomStyle);

    const ZONE_URL = '/zones';
    const SIM_CARD_SERVICE_TYPE_TO_ZONE_CODE_URL = '/sim_card_service_type_to_zone_codes';

    const dispatch = useDispatch();
    const classes = useStyles();
    const tableStickyHeader = { root: classes.tableStickyHeader };

    const [afterFirstLoad, setAfterFirstLoad] = useState<boolean>(false);
    const [filters, setFilters] = useState<Array<Filter>>([{ name: 'customZoneType', value: 2 }]);
    const [orders, setOrders] = useState<Array<Order>>([]);
    const [zones, setZones] = useState<any>(null);
    const [selectedZoneFilters, setSelectedZoneFilters] = useState<Array<Filter>>([]);
    const [selectedZoneOrders, setSelectedZoneOrders] = useState<Array<Order>>([]);
    const [selectedZone, setSelectedZone] = useState<Array<number>>([]);
    const [selectedZoneObjects, setSelectedZoneObjects] = useState<any>(null);
    const [reload, setReload] = useState<boolean>(false);

    const firstSearch = useCallback(() => {
        const itemLoader = new ItemLoaders(dispatch);

        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(ZONE_URL)),
            DISCOUNT_CONSTANTS.LIST,
            filters,
            orders,
            (data: any) => {
                setZones(data);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    }, [dispatch, filters, orders]);

    const clearSearch = useCallback(() => {
        const itemLoader = new ItemLoaders(dispatch);

        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(ZONE_URL)),
            DISCOUNT_CONSTANTS.LIST,
            [],
            orders,
            (data: any) => {
                setZones(data);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    }, [dispatch, orders]);

    const loadSelectedZoneCodes = useCallback(() => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(SIM_CARD_SERVICE_TYPE_TO_ZONE_CODE_URL)),
            DISCOUNT_CONSTANTS.LIST,
            [
                { name: 'simCardServiceType', value: simCardServiceTypeId },
                { name: 'notRemove', value: true },
                { name: 'pagination', value: false },
            ],
            [],
            (data: any) => {
                if (data && data['hydra:member']) {
                    const assignedZoneIds: Array<number> = [];
                    const selectedObjects = {
                        'hydra:member': data['hydra:member'].map((el: any) => ({
                            id: el.id,
                            zoneId: el.zone?.id || el.zoneId,
                            zoneName: el.zone?.mainname || el.mainname || el.zoneName,
                            simCardServiceTypeToZoneCodeId: el.id,
                        })),
                    };

                    data['hydra:member'].forEach((el: any) => {
                        assignedZoneIds.push(el.zone?.id || el.zoneId);
                    });

                    setSelectedZone(assignedZoneIds);
                    setSelectedZoneObjects(selectedObjects);
                } else {
                    setSelectedZone([]);
                    setSelectedZoneObjects({ 'hydra:member': [] });
                }
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    }, [dispatch, simCardServiceTypeId]);

    const clearFilter = (): void => {

        dispatch({
            type: FilterListReducerFieldName.HEADER_FILTER_MANAGE,
        });
        dispatch({
            type: FilterReducerFieldName.HEADER_FILTER_MANAGE,
            clearAll: true,
        });

        setFilters([{ name: 'customZoneType', value: 2 }]);
        setSelectedZoneFilters([]);
        setOrders([]);

        setReload(true)
    };


    useEffect(() => {
        if(reload){
            clearSearch();
            setReload(false)
        }
    }, [reload,clearSearch]);

    const updateOrderList = (orders: Array<Order>): void => setOrders(orders);
    const updateFilterList = (filters: Array<Filter>): void => setFilters(filters);

    const paginationCallbackFunction = (url: string): void => {
        const itemLoader = new ItemLoaders(dispatch);
        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(url),
            DISCOUNT_CONSTANTS.LIST,
            [],
            orders,
            (data: any) => {
                setZones(data);
            },
            { propagateDataToCallback: true, dispatchAfterCallback: false }
        );
    };

    const updateSelectedZonesOrderList = (orders: Array<Order>): void => {
        setSelectedZoneOrders(orders);
    };

    const updateSelectedZoneFiltersList = (filters: Array<Filter>): void => {
        setSelectedZoneFilters(filters);
    };

    const saveToApi = (item: any): void => {
        const itemLoader = new ItemLoaders(dispatch);

        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(SIM_CARD_SERVICE_TYPE_TO_ZONE_CODE_URL, configDefault.PLATFORM_API_PREFIX)),
            platformGroupsConstants.ADD,
            item,
            (data: any): void => {
                if (data && data.id) {
                    const updatedItem = {
                        id: data.id,
                        zoneId: data.zoneId,
                        zoneName: data.zone?.mainname,
                        simCardServiceType: data.simCardServiceType,
                    };

                    if (data.zoneId && !selectedZone.includes(data.zoneId)) {
                        setSelectedZone([...selectedZone, data.zoneId]);
                    }

                    const newSelected = { ...selectedZoneObjects };
                    if (newSelected && !newSelected['hydra:member']) {
                        newSelected['hydra:member'] = [];
                    }
                    newSelected['hydra:member'].unshift(updatedItem);
                    setSelectedZoneObjects(newSelected);
                }
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: true,
            }
        );
    };

    const removeFromApi = (item: any): void => {
        const itemLoader = new ItemLoaders(dispatch);

        itemLoader.deleteWithCallback(
            FormGeneralHelper.getByUri(
                FormGeneralHelper.getPath(SIM_CARD_SERVICE_TYPE_TO_ZONE_CODE_URL, configDefault.PLATFORM_API_PREFIX) + '/' + item.id
            ),
            platformGroupsConstants.ADD,
            (data: any): void => {
                if (selectedZone.includes(item.zoneId)) {
                    let newElements = selectedZone.filter((zoneId: number) => {
                        return zoneId !== item.zoneId;
                    });
                    setSelectedZone(newElements);

                    if (selectedZoneObjects && selectedZoneObjects['hydra:member']) {
                        let newSelectedArr: Array<any> = [];
                        const newSelected = { ...selectedZoneObjects };
                        selectedZoneObjects['hydra:member'].map((el: any) => {
                            if (el.zoneId !== item.zoneId) {
                                newSelectedArr.push(el);
                            }
                        });

                        newSelected['hydra:member'] = newSelectedArr;
                        setSelectedZoneObjects(newSelected);
                    }
                }
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: true,
            }
        );
    };

    const addToZone = (item: any): void => {
        if (!selectedZone.includes(item.id)) {
            const newItem = {
                zoneId: item.id,
                zone: {
                    id: FormGeneralHelper.changeAdescomApiIriToBaseApiIri(item['@id']),
                    mainname: item.mainname,
                },
                simCardServiceType: FormGeneralHelper.changeAdescomApiIriToBaseApiIri(
                    `/adescom-api/api/platform/sim_card_service_types/${simCardServiceTypeId}`
                ),
            };

            saveToApi(newItem);
        }
    };

    const removeFromZone = (item: any): void => {
        if (selectedZone.includes(item.zoneId)) {
            removeFromApi(item);
        }
    };

    useEffect(() => {
        if (!afterFirstLoad) {
            setAfterFirstLoad(true);
            firstSearch();
            loadSelectedZoneCodes();
        }
    }, [firstSearch, loadSelectedZoneCodes, afterFirstLoad]);

    const sort = (a: any, b: any): any => {
        let sortColumn = null;
        let sortColumnValue = null;
        if (selectedZoneOrders.length === 1) {
            sortColumn = selectedZoneOrders[0].name;
            sortColumnValue = selectedZoneOrders[0].value;
        }

        if (sortColumn !== null && sortColumnValue !== null) {
            if (a.zoneName.toLowerCase() < b.zoneName.toLowerCase()) {
                return sortColumnValue === 'asc' ? -1 : 1;
            }
            if (a.zoneName.toLowerCase() > b.zoneName.toLowerCase()) {
                return sortColumnValue === 'asc' ? 1 : -1;
            }
            return 0;
        }

        return 0;
    };

    return (
        <GridContainer spacing={2} style={{ marginTop: '20px' }}>
            <GridItem xs={12} md={7}>
                <Card>
                    <CardBody>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <GridContainer>
                                        <GridItem xs={12}>
                                            <CustomTextButtom size={'lg'} callback={firstSearch} callbackParam={{}} label="general:common.search" />
                                            {(filters.length > 0 || orders.length > 0) && (
                                                <CustomTextButtom
                                                    callback={clearFilter}
                                                    callbackParam={{}}
                                                    color={'primary'}
                                                    size={'lg'}
                                                    label="general:common.clear"
                                                />
                                            )}
                                            <FilterList />
                                        </GridItem>
                                    </GridContainer>
                                </TableRow>
                                <TableRow>
                                    <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                        <TableHeaderContentCell textAlign="left" title="notifications:welcome_sms.actions" />
                                    </TableCell>
                                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                                        <TableHeaderContentCell
                                            textAlign="left"
                                            title="notifications:welcome_sms.zone_name"
                                            fieldName="mainname"
                                            showSort={true}
                                            onlyFilter={false}
                                            defaultDirection="desc"
                                            searchFieldType="string"
                                            filters={filters}
                                            sort={orders}
                                            updateOrderList={updateOrderList}
                                            updateFilterList={updateFilterList}
                                            searchActionCallback={firstSearch}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {zones &&
                                    zones['hydra:member'].map((item: any) => {
                                        if (!item.mainname) {
                                            return null;
                                        }
                                        return (
                                            <TableRow key={item.id}>
                                                <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                                    <AddButton callback={addToZone} disabled={selectedZone.includes(item.id)} callbackParam={item} />
                                                </TableCell>
                                                <TableCell classes={tableStickyHeader} padding="none" align="center">
                                                    <div
                                                        style={{
                                                            wordWrap: 'break-word',
                                                            whiteSpace: 'normal',
                                                            lineHeight: '1.2',
                                                            padding: '8px 4px',
                                                        }}
                                                    >
                                                        {item.mainname}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        {zones && zones['hydra:view'] && <Pager callback={paginationCallbackFunction} views={zones} />}
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </CardBody>
                </Card>
            </GridItem>

            <GridItem xs={12} md={5}>
                <Card>
                    <CardBody style={{ maxHeight: 980, overflow: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <GridContainer>
                                        <GridItem xs={12}>
                                            <FilterList />
                                        </GridItem>
                                    </GridContainer>
                                </TableRow>
                                <TableRow>
                                    <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                        <TableHeaderContentCell textAlign="left" title="notifications:welcome_sms.actions" />
                                    </TableCell>
                                    <TableCell classes={tableStickyHeader} padding="none" align="center">
                                        <TableHeaderContentCell
                                            textAlign="left"
                                            title="notifications:welcome_sms.zone_name"
                                            fieldName="zoneName"
                                            showSort={true}
                                            onlyFilter={false}
                                            defaultDirection="desc"
                                            searchFieldType="string"
                                            filters={selectedZoneFilters}
                                            sort={selectedZoneOrders}
                                            updateOrderList={updateSelectedZonesOrderList}
                                            updateFilterList={updateSelectedZoneFiltersList}
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedZoneObjects && selectedZoneObjects['hydra:member'] && selectedZoneObjects['hydra:member'].length > 0 ? (
                                    selectedZoneObjects['hydra:member'].sort(sort).map((item: any, index: number) => {
                                        let show = true;
                                        selectedZoneFilters.forEach((filter) => {
                                            if (filter.name === 'zoneName' && item.zoneName) {
                                                const filterValue = String(filter.value).toLowerCase();
                                                if (!item.zoneName.toLowerCase().includes(filterValue)) {
                                                    show = false;
                                                }
                                            }
                                        });

                                        if (!show) {
                                            return null;
                                        }

                                        return (
                                            <TableRow key={`selected-${item.id}`}>
                                                <TableCell classes={tableStickyHeader} padding="none" align="center" style={{ width: 30 }}>
                                                    <RemoveButton callback={removeFromZone} callbackParam={item} />
                                                </TableCell>
                                                <TableCell classes={tableStickyHeader} padding="none" align="center">
                                                    <div
                                                        style={{
                                                            wordWrap: 'break-word',
                                                            whiteSpace: 'normal',
                                                            lineHeight: '1.2',
                                                            padding: '8px 4px',
                                                        }}
                                                    >
                                                        {item.zoneName}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center" style={{ padding: '20px' }}></TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};
